import { useEffect, useRef, useState } from 'react';
import { getCookie, Key } from '@/utils/cookie';

export interface NoticePush {
  type: 'new_notice';
  title: string;
  content: string;
  category: string;
  refId?: number;
  refType?: string;
}

// 重连退避序列，超出后保持最后一档；附加随机抖动避免集群同步重连
const RECONNECT_DELAYS_MS = [3000, 6000, 12000, 30000];
const RECONNECT_JITTER_MS = 1000;

/**
 * 站内通知实时推送 WebSocket
 * - 收到 new_notice 时通过 onNotice 回调投递
 * - 指数退避自动重连（网络恢复时立即重连）+ 心跳保活
 */
export function useNoticeWS(onNotice: (msg: NoticePush) => void) {
  const [connected, setConnected] = useState(false);
  const onNoticeRef = useRef(onNotice);
  onNoticeRef.current = onNotice;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const reconnectAttemptRef = useRef(0);

  useEffect(() => {
    let closed = false;

    const connect = () => {
      if (closed) return;
      const token = getCookie(Key.ATK) || '';
      const proto = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // mako dev server 会把所有 WS 升级请求转给 HMR 服务，无法代理业务 WS，
      // 开发环境直连后端，地址与 config/proxy.ts 共用 UMI_APP_DEV_API_TARGET
      const base =
        process.env.NODE_ENV === 'development'
          ? (process.env.UMI_APP_DEV_API_TARGET || 'http://127.0.0.1:6600').replace(
              /^http/,
              'ws',
            )
          : `${proto}://${window.location.host}`;
      const url = `${base}/api/sys_notification/ws?token=${encodeURIComponent(token)}`;

      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        reconnectAttemptRef.current = 0;
        if (pingTimerRef.current) clearInterval(pingTimerRef.current);
        pingTimerRef.current = setInterval(() => {
          try {
            ws.send(JSON.stringify({ type: 'ping' }));
          } catch {}
        }, 25000);
      };

      ws.onmessage = (e) => {
        let data: NoticePush | { type: string };
        try {
          data = JSON.parse(e.data);
        } catch {
          return;
        }
        if (data.type === 'new_notice') {
          onNoticeRef.current(data as NoticePush);
        }
      };

      ws.onclose = () => {
        setConnected(false);
        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current);
          pingTimerRef.current = null;
        }
        if (!closed) {
          const step = Math.min(
            reconnectAttemptRef.current,
            RECONNECT_DELAYS_MS.length - 1,
          );
          reconnectAttemptRef.current += 1;
          const delay =
            RECONNECT_DELAYS_MS[step] +
            Math.floor(Math.random() * RECONNECT_JITTER_MS);
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    };

    connect();

    // 网络恢复时跳过剩余退避，立即重连
    const onOnline = () => {
      const state = wsRef.current?.readyState;
      if (state === WebSocket.OPEN || state === WebSocket.CONNECTING) return;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectAttemptRef.current = 0;
      connect();
    };
    window.addEventListener('online', onOnline);

    return () => {
      closed = true;
      window.removeEventListener('online', onOnline);
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (pingTimerRef.current) clearInterval(pingTimerRef.current);
      try {
        wsRef.current?.close();
      } catch {}
      wsRef.current = null;
    };
  }, []);

  return { connected };
}
